import { ArrowLeftRight, Check, Minus } from 'lucide-react'
import type { SettlementSummary } from '@/api/people'
import { Button } from '@/components/ui/button'
import { useListAmountFormat } from '@/hooks/useFormatCurrency'
import { formatShortDate } from '@/lib/formatDate'
import { cn } from '@/lib/utils'
import { DirectionIcon } from './OpenItems'
import { sourceLabel, type HistoryMonth } from './peopleStats'
import { PEOPLE_CARD, PEOPLE_SECTION_LABEL } from './peopleUi'

function SettlementIcon({ direction }: { direction: SettlementSummary['direction'] }) {
  const Icon = direction === 'WRITE_OFF' ? Minus : direction === 'EVEN' ? ArrowLeftRight : Check
  return (
    <span
      aria-hidden="true"
      className={cn(
        'grid h-8 w-8 shrink-0 place-items-center rounded-full',
        direction === 'WRITE_OFF'
          ? 'bg-[var(--fl-warning-bg)] text-[var(--fl-warning)]'
          : direction === 'EVEN'
            ? 'bg-[var(--fl-surface-subtle)] text-[var(--fl-text-2)]'
            : 'bg-[var(--fl-primary-subtle)] text-[var(--fl-primary-text)]'
      )}
    >
      <Icon size={16} />
    </span>
  )
}

export function PersonHistory({
  name,
  months,
  onUndo,
}: {
  name: string
  months: HistoryMonth[]
  /** Omitted in read-only mode, which hides Undo. */
  onUndo?: (s: SettlementSummary) => void
}) {
  const format = useListAmountFormat()
  const describe = (s: SettlementSummary) =>
    s.direction === 'IN'
      ? `${name} paid you ${format(s.netAmount)}`
      : s.direction === 'OUT'
        ? `You paid ${name} ${format(s.netAmount)}`
        : s.direction === 'WRITE_OFF'
          ? `Written off ${format(s.writtenOff ?? 0)} — no money moved`
          : 'Evened out — no money moved'

  return (
    <section aria-label="History">
      <h2 className={PEOPLE_SECTION_LABEL}>History</h2>
      <div className={cn(PEOPLE_CARD, '[&>div:last-child_li:last-child]:border-b-0')}>
        {months.map((m) => (
          <div key={m.label}>
            <p className="border-b border-[var(--fl-divider)] bg-[var(--fl-surface-hover)] px-4 py-2.5 text-xs font-semibold text-[var(--fl-text-2)]">
              {m.label}
            </p>
            <ul>
              {m.items.map((item) =>
                item.kind === 'settlement' ? (
                  <li
                    key={item.id}
                    className="flex items-center gap-3 border-b border-[var(--fl-divider)] px-4 py-3"
                  >
                    <SettlementIcon direction={item.settlement.direction} />
                    <span className="min-w-0 flex-1">
                      <span className="block text-sm text-[var(--fl-text-1)]">
                        {describe(item.settlement)}
                      </span>
                      <span className="block text-xs text-[var(--fl-text-3)]">
                        {formatShortDate(item.date)} · Settlement
                      </span>
                    </span>
                    {onUndo && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => onUndo(item.settlement)}
                        className="h-8 text-xs text-[var(--fl-text-2)]"
                      >
                        Undo
                      </Button>
                    )}
                  </li>
                ) : (
                  <li
                    key={item.id}
                    className="flex items-center gap-3 border-b border-[var(--fl-divider)] px-4 py-3 text-[var(--fl-text-3)]"
                  >
                    <DirectionIcon owedToUs={item.entry.direction === 'OWED_TO_US'} muted />
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm text-[var(--fl-text-2)]">
                        {item.entry.description}
                      </span>
                      <span className="block text-xs">
                        {formatShortDate(item.date)} · {sourceLabel(item.entry, name)} · settled
                      </span>
                    </span>
                    <span className="text-sm tabular-nums line-through">
                      {format(item.entry.amount)}
                    </span>
                  </li>
                )
              )}
            </ul>
          </div>
        ))}
      </div>
    </section>
  )
}
